import { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useAuth, type SignupRole } from "@/hooks/useAuth";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { KanfyLogo } from '@/components/KanfyLogo';
import { AuthForm, type EmailView } from '@/components/auth/AuthForm';
import { safeInternalRedirect } from '@/lib/safeRedirect';
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Auth() {
  usePageMeta("Sign In | Kanfy");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  /*
   * Originally #776: this read `mode === "signup"` and nothing else, so every
   * other way the app actually links here fell through to SIGN IN. Both
   * `type` and `mode` are still accepted (in-app callers send `type`, the
   * public CTA sends `mode`), and the PATH is consulted too, so /signup opens
   * signup with no query string at all.
   *
   * What changed since: `signupRole` is no longer shown to the client as a
   * banner or a switch — the signup FORM shows no role concept at all
   * (Option A). It's still resolved and passed through here, silently, for
   * the legitimate deep-link case that already knows the answer — e.g.
   * HelperProfileCreation.tsx re-opening /auth?type=helper for someone
   * re-authenticating mid-application. An ordinary signup with no role
   * param reaches signUp() with `signupRole` undefined, which is deliberate:
   * see useAuth.ts's signUp() for why that must never default to 'client'.
   */
  const roleParam = (searchParams.get("type") ?? searchParams.get("mode") ?? "").toLowerCase();
  const signupRole: SignupRole | undefined =
    roleParam === "helper" ? "helper" : roleParam === "client" ? "client" : undefined;
  const wantsSignup =
    roleParam === "signup" || signupRole !== undefined || location.pathname === "/signup";
  const initialEmailView: EmailView = wantsSignup ? "signup" : "signin";

  // Only allow same-origin internal paths — see lib/safeRedirect, shared with
  // /post-auth so the two can never drift apart.
  const redirectTo = safeInternalRedirect(searchParams.get("redirect")) ?? "/post-auth";

  useEffect(() => {
    if (user && !loading) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, loading, navigate, redirectTo]);

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center py-10 pt-[max(1rem,env(safe-area-inset-top,1rem))] pb-[max(2rem,env(safe-area-inset-bottom,2rem))] bg-background">
      <div className="flex items-center justify-between px-5 pb-3 w-full max-w-md">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Back to Home"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="text-sm font-medium">{t('authBackHome')}</span>
        </button>
        <KanfyLogo variant="primary" size="sm" />
      </div>

      {/* Same container AuthModal renders its content in, so the page and the
          inline modal are visually identical — see AuthForm.tsx. */}
      <div className="px-5 w-full max-w-md">
        <div className="bg-card border border-border rounded-3xl shadow-lg p-6">
          <AuthForm
            initialEmailView={initialEmailView}
            signupRole={signupRole}
            onSuccess={() => navigate(redirectTo, { replace: true })}
            // This page already renders its own logo above (line 61); without
            // this, AuthForm's internal "Welcome to Kanfy" header doubled it.
            showHeader={false}
          />
        </div>
      </div>
    </div>
  );
}
